import styles from './report.module.scss';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Scorecard from './Scorecard';

import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNaFetching } from 'hooks/useNaFetching';
import { useLocation } from 'react-router-dom';
import { addThousandSeparator, addPlusMinus5PercentageRangeToNumber } from 'utils/numbers';
import { getUnits } from 'utils/units';
import { getFormatSeparatorsOptions, decimalPrecision } from 'config/marketConfig';
import searchParams from 'constants/searchParams';
import { pdfPageMaxRows } from 'config/pdfReport';

export default function Report() {
  let rowCount = 0;
  const { t } = useTranslation();
  const spaces = useSelector((state) => state.na.spaces);
  const workplacestyleSelected = useSelector((state) => state.na.workplacestyleSelected);
  const workplaceStyle = spaces.find((space) => space.id === workplacestyleSelected);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const naId = params.get(searchParams.id);
  const apiToken = params.get(searchParams.apiToken);
  const externalAccessKey = params.get(searchParams.externalAccessKey);

  const [, { fetchReportNa }] = useNaFetching();

  useEffect(() => {
    fetchReportNa(externalAccessKey, naId, apiToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const increaseRowCount = () => rowCount++;
  const getContinuedHeader = (spaceType) => {
    if (rowCount % pdfPageMaxRows === 0) {
      increaseRowCount();
      return (
        <TableRow
          key={`${spaceType.id}_${rowCount}`}
          className={styles.pdf__space_type_title}
          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
          <TableCell component="td" scope="row" colSpan={4}>
            {`${t(spaceType.name)} (continued)`}
          </TableCell>
        </TableRow>
      );
    }
  };
  const getFormattedSize = (space) => {
    const units = getUnits().unit;
    const width = Number(space.width);
    const height = Number(space.height);
    const formatSeparator = { fixed: decimalPrecision, ...getFormatSeparatorsOptions() };

    if (isNaN(width) || isNaN(height)) {
      return '';
    }

    const formattedWidth = addThousandSeparator(width, formatSeparator);
    const formattedHeight = addThousandSeparator(height, formatSeparator);

    return `${t(space.name)} - ${formattedWidth + units} x ${formattedHeight + units}`;
  };

  return (
    <div className={styles.report_wrapper}>
      <main className={styles.main}>
        <Scorecard data-testid="ReportScorecard" />
        <Table
          data-testid="ReportTable"
          className={styles.pdf__table}
          sx={{ minWidth: 650 }}
          aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell className={styles.pdf__table_title} colSpan={4}>
                {t('report.spaceDetails')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={styles.pdf__columns_title}>{t('report.category')}</TableCell>
              <TableCell className={styles.pdf__columns_title}>{t('report.type')}</TableCell>
              <TableCell className={styles.pdf__columns_title}>{t('report.units')}</TableCell>
              <TableCell className={styles.pdf__columns_title} align="right">
                {t('report.area')}
              </TableCell>
            </TableRow>
          </TableHead>
          {workplaceStyle?.spaceTypes.map((spaceType) => {
            const showSpaceType = spaceType?.spaces?.some((space) => space?.quantity > 0);
            if (!showSpaceType) return null;
            increaseRowCount();
            return (
              <TableBody key={spaceType.id}>
                <TableRow
                  className={styles.pdf__space_type_title}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="td" scope="row" colSpan={4}>
                    {t(spaceType.name)}
                  </TableCell>
                </TableRow>
                {spaceType.spaces.map((space) => {
                  if (space.quantity > 0) {
                    increaseRowCount();
                    return (
                      <>
                        {getContinuedHeader(spaceType)}
                        <TableRow
                          className={styles.pdf__space_text}
                          key={space.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell component="td" scope="row"></TableCell>
                          <TableCell component="td" scope="row">
                            {t(space.name)} -{' '}
                            {`${addThousandSeparator(space.size.width, {
                              fixed: decimalPrecision,
                              ...getFormatSeparatorsOptions(),
                            }) + getUnits().unit
                              } x ${addThousandSeparator(space.size.height, {
                                fixed: decimalPrecision,
                                ...getFormatSeparatorsOptions(),
                              }) + getUnits().unit
                              }`}
                          </TableCell>
                          <TableCell component="td" scope="row">
                            {addThousandSeparator(space.quantity, getFormatSeparatorsOptions())}
                          </TableCell>
                          <TableCell component="td" scope="row" align="right">
                            {addThousandSeparator(
                              space.size.width * space.size.height * space.quantity,
                              { fixed: decimalPrecision, ...getFormatSeparatorsOptions() },
                            )}
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  }
                  return null;
                })}
              </TableBody>
            );
          })}
          {workplaceStyle?.customSpaces.length > 0 && increaseRowCount() > 0 && (
            <TableBody>
              <TableRow
                className={styles.pdf__space_type_title}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component="td" scope="row" colSpan={4}>
                  {t('report.customSpaces')}
                </TableCell>
              </TableRow>
              {workplaceStyle?.customSpaces.map((space) => {
                increaseRowCount();
                return (
                  <>
                    {getContinuedHeader({ id: Date.now(), name: 'Custom Specialty Spaces' })}
                    <TableRow
                      className={styles.pdf__space_text}
                      key={space.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="td" scope="row"></TableCell>
                      <TableCell component="td" scope="row">
                        {getFormattedSize(space)}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {addThousandSeparator(space.quantity, {
                          ...getFormatSeparatorsOptions(),
                        })}
                      </TableCell>
                      <TableCell component="td" scope="row" align="right">
                        {addThousandSeparator(space.width * space.height * space.quantity, {
                          fixed: decimalPrecision,
                          ...getFormatSeparatorsOptions(),
                        })}
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>
          )}
        </Table>
        <div className={styles.pdf__summary_wrapper}>
          <div className={styles.pdf__summary_container}>
            <div className={styles.pdf__summary_row}>
              <div className={styles.pdf__summary_title}>{t('report.netProgramArea')}</div>
              <div className={styles.pdf__summary_value}>
                {addThousandSeparator(workplaceStyle?.netProgramSqFootage, {
                  fixed: decimalPrecision,
                  ...getFormatSeparatorsOptions(),
                })}
              </div>
            </div>
            <div className={styles.pdf__summary_row}>
              <div className={styles.pdf__summary_title}>
                {t('report.circulation')} ({workplaceStyle?.circulationMultiplier}%)
              </div>
              <div className={styles.pdf__summary_value}>
                {addThousandSeparator(
                  (workplaceStyle?.netProgramSqFootage * workplaceStyle?.circulationMultiplier) /
                  100,
                  {
                    fixed: decimalPrecision,
                    ...getFormatSeparatorsOptions(),
                  },
                )}
              </div>
            </div>
            <div className={styles.separator}></div>
            <div className={styles.pdf__summary_row}>
              <div className={styles.pdf__summary_title}>{t('report.usableArea')}</div>
              <div className={styles.pdf__summary_value}>
                {addThousandSeparator(workplaceStyle?.usableSquareFeet, {
                  fixed: decimalPrecision,
                  ...getFormatSeparatorsOptions(),
                })}
              </div>
            </div>
            <div className={styles.pdf__summary_row}>
              <div className={styles.pdf__summary_title}>
                {t('report.buildingGrossUpFactor')} (
                {addThousandSeparator(workplaceStyle?.buildingGrossUpFactor, {
                  fixed: decimalPrecision,
                  ...getFormatSeparatorsOptions(),
                })}
                %)
              </div>
              <div className={styles.pdf__summary_value}>
                {addThousandSeparator(
                  (workplaceStyle?.usableSquareFeet * workplaceStyle?.buildingGrossUpFactor) / 100,
                  {
                    fixed: decimalPrecision,
                    ...getFormatSeparatorsOptions(),
                  },
                )}
              </div>
            </div>
            <div className={styles.separator}></div>
            <div className={styles.pdf__summary_row}>
              <div className={styles.pdf__summary_title}>{t('report.rentalArea')}</div>
              <div className={styles.pdf__summary_value}>
                {addThousandSeparator(workplaceStyle?.rentableSquareFeet, {
                  fixed: decimalPrecision,
                  ...getFormatSeparatorsOptions(),
                })}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pdf__rental_wrapper}>
          <div className={styles.pdf__rental_row}>
            <div className={styles.pdf__summary_title}>{t('report.recommendedRent')}</div>
            <div className={styles.pdf__summary_value}>
              {addPlusMinus5PercentageRangeToNumber(
                workplaceStyle?.rentableSquareFeet,
                getFormatSeparatorsOptions(),
              )}{' '}
              {getUnits().area}
            </div>
          </div>
        </div>
        <p className={styles.pdf__bottom_text}>{t('report.recommendedSize')}</p>
        <p className={styles.pdf__bottom_text}>{t('report.buildingUpInfo')}</p>
      </main>
    </div>
  );
}
